import React from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import MapIcon from "@mui/icons-material/Map";
import PaymentsIcon from "@mui/icons-material/Payments";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SettingsIcon from "@mui/icons-material/Settings";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ShieldIcon from "@mui/icons-material/Shield";
import { useAuth } from "../../context/AuthContext";
import { brand, neutral as neutralTokens, shellGradient, text } from "../../theme/tokens";

/**
 * Role-aware navigation.
 *  - superadmin → platform console (Overview, Organizations) — routes under /superadmin
 *  - admin      → everything under /dashboard
 *  - collector  → Dashboard, Mob Customers, Mob Payments, Reports
 *                 (the desktop Customers/Maps pages are admin-only —
 *                 collectors use the mobile-first Mob Customers/Mob Payments
 *                 worklists for the same jobs; within Reports, collectors
 *                 only get the Collections tab — see ReportsLayout — scoped
 *                 to their own collection totals)
 *
 * The superadmin set is separate because a platform operator manages
 * organizations, not an org's own customers/payments.
 *
 * HIDDEN (owner's call): the **Complaints** and **Messages** entries are
 * deliberately absent from this list. Their routes are untouched and still
 * resolve — `/dashboard/complaints` and `/dashboard/messages` work if typed or
 * bookmarked — they simply aren't advertised in the nav. To bring either back,
 * restore its entry below; nothing else has to change. (Payments was hidden
 * too, and restored to its original slot on 2026-09-10.)
 *
 * Trash sits last: it is where deleted customers, payments, STBs, collectors
 * and Settings masters go, and where they are restored from.
 */
const NAV_ITEMS = [
  { label: "Dashboard", to: "/dashboard", icon: <DashboardIcon />, end: true, roles: ["admin", "collector"] },
  { label: "Customers", to: "/dashboard/customers", icon: <PeopleAltIcon />, roles: ["admin"] },
  { label: "Maps", to: "/dashboard/maps", icon: <MapIcon />, roles: ["admin"] },
  { label: "Payments", to: "/dashboard/payments", icon: <PaymentsIcon />, roles: ["admin"] },
  { label: "Mob Customers", to: "/dashboard/mob-customers", icon: <ContactPhoneIcon />, roles: ["admin", "collector"] },
  { label: "Mob Payments", to: "/dashboard/mob-payments", icon: <PriceCheckIcon />, roles: ["admin", "collector"] },
  { label: "Billing", to: "/dashboard/bills", icon: <ReceiptLongIcon />, roles: ["admin"] },
  { label: "Reports", to: "/dashboard/reports", icon: <AssessmentIcon />, roles: ["admin", "collector"] },
  { label: "Settings", to: "/dashboard/settings", icon: <SettingsIcon />, roles: ["admin"] },
  { label: "Trash", to: "/dashboard/trash", icon: <DeleteOutlineIcon />, roles: ["admin"] },
];

const SUPERADMIN_NAV_ITEMS = [
  { label: "Overview", to: "/superadmin", icon: <DashboardIcon />, end: true, roles: ["superadmin"] },
  { label: "Organizations", to: "/superadmin/orgs", icon: <ApartmentIcon />, roles: ["superadmin"] },
];

const ACTIVE = brand.amber;
const IDLE = text.onDarkMuted;

const Sidebar = ({ onNavigate }) => {
  const { pathname } = useLocation();
  const { organization, role } = useAuth();

  const isSuper = role === "superadmin";
  const source = isSuper ? SUPERADMIN_NAV_ITEMS : NAV_ITEMS;
  const items = source.filter((item) => item.roles.includes(role));

  const isActive = (item) =>
    item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`);

  return (
    <Box
      sx={{
        width: 240,
        height: "100%",
        minHeight: "100dvh",
        backgroundImage: shellGradient,
        // The gradient ends on the same purple the content area now uses, so
        // without this hairline the sidebar and the page merge into one block
        // wherever the two meet low down the screen.
        borderRight: `1px solid ${neutralTokens.border}`,
        display: "flex",
        flexDirection: "column",
        py: 2,
      }}
    >
      <Box sx={{ px: 2.5, pb: 1.5 }}>
        <Typography
          sx={{ color: text.onDark, fontWeight: 700, fontSize: 22, lineHeight: 1.2, display: "flex", alignItems: "center", gap: 0.75 }}
          noWrap
        >
          {isSuper ? <ShieldIcon sx={{ color: ACTIVE, fontSize: 22 }} /> : null}
          Cable CRM
        </Typography>
        <Typography sx={{ color: text.onDarkMuted, fontSize: 13 }} noWrap>
          {isSuper ? "Platform Console" : organization?.name || ""}
        </Typography>
      </Box>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 1 }} />

      <List sx={{ px: 1.5, flexGrow: 1, minHeight: 0, overflowY: "auto" }} component="nav">
        {items.map((item) => {
          const active = isActive(item);
          return (
            <ListItemButton
              key={item.to}
              component={Link}
              to={item.to}
              onClick={onNavigate}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                minHeight: 44,
                color: active ? ACTIVE : IDLE,
                backgroundColor: active ? "rgba(251,189,44,0.12)" : "transparent",
                transition: "background-color 200ms ease, color 200ms ease",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: active ? ACTIVE : IDLE }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: active ? 600 : 500 }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};

export default Sidebar;
